<script setup lang="ts">
import {computed} from 'vue'

import type {PrecalcButton} from './harmonika.types'

/**
 * Ein einzelner Harmonika-Knopf. Zwei Darstellungen über `variant`:
 * - `inspector` (Default): kleine Knöpfe der Griff-Visualisierung (Ausgabe-Vorschau); aktiver
 *   Knopf blau (`bg-sky-300`), Vorschau-Knopf bernstein (`isPreview`).
 * - `simulator`: große, spielbare Knöpfe des Keyboard-Simulators; aktiver (gedrückter) Knopf rot.
 * Die Knopf-LOGIK (Gleicher-Ton-Hinweise) ist für beide identisch.
 */
const {
  rowIdx,
  btnIdx,
  btnData,
  variant = 'inspector',
  isActive = false,
  isPreview = false,
  label = ' ',
  keyLabel = '',
  activeMidiNotes,
} = defineProps<{
  rowIdx: number
  btnIdx: number
  btnData: PrecalcButton
  variant?: 'inspector' | 'simulator'
  isActive?: boolean
  isPreview?: boolean
  label?: string
  keyLabel?: string
  activeMidiNotes: Set<number>
}>()

const emit = defineEmits<{
  activate: [rowIdx: number, btnIdx: number]
  deactivate: [rowIdx: number, btnIdx: number]
}>()

const showHints = computed(() => activeMidiNotes.size > 0 && !isActive)
const matchesDruck = computed(() => showHints.value && btnData.druckMidis.some((midi) => activeMidiNotes.has(midi)))
const matchesZug = computed(() => showHints.value && btnData.zugMidis.some((midi) => activeMidiNotes.has(midi)))
const isGleichTonOrOrientierungston = computed(() => (rowIdx !== 0 && btnIdx === 5) || (rowIdx === 0 && btnIdx === 6))


// Größe je Variante: Inspector klein (Vorschau-Grid), Simulator groß (spielbar).
// Simulator (`SIM_BTN`): die Knopfgröße folgt der Breite des `@container` in `InstrumentBody`, so
// dass die längste Reihe (13 Knöpfe + 2×0.33 Knopf Einrückung + 14 Lücken à 0.5rem) genau passt –
// zwischen 1.75rem und 3rem; darunter scrollt der Korpus. `InstrumentBody::rowIndentSpace` rechnet
// mit demselben Ausdruck.
const sizeClasses = computed(() =>
    variant === 'simulator'
        ? 'flex size-[clamp(1.75rem,calc((100cqw_-_7rem)/13.67),3rem)] shrink-0 md:text-[10px]'
        : 'inline-flex max-h-8 max-w-8 h-8 w-8 aspect-square  md:text-[10px]',
)

// Zustands-Klassen je Variante. Inspector: Vorschau (bernstein) > aktiv (blau) > Orientierung >
// Basis. Simulator: aktiv (rot) + Orientierung/Basis (kein Vorschau-Zustand).
// Die beiden Aktiv-Regeln MÜSSEN sich gegenseitig ausschließen (`variant`-Gate auf BEIDEN,
// kritisch – nicht rückbauen): ohne das Gate auf der Inspector-Regel sind bei einem gedrückten
// Simulator-Knopf beide Bedingungen wahr, Vue rendert `bg-red-500` UND `bg-sky-300`, und welche
// gewinnt entscheidet allein die Reihenfolge im generierten Tailwind-CSS – der gedrückte Knopf
// erschien dadurch im Auswahl-Blau des Inspectors statt im Rot des Simulators.
const stateClasses = computed(() => {

  return {
    'translate-y-1 border-red-400 bg-red-500 text-red-900 shadow-xl': isActive && variant === 'simulator',
    'bg-amber-300 text-amber-900 shadow-lg ring-2 ring-amber-900': isPreview,
    'border-3 border-teal-200 bg-teal-200': isGleichTonOrOrientierungston.value && !isActive && !isPreview,
    'translate-y-1 border-red-400 bg-sky-300 text-gray-900 shadow-xl': isActive && !isPreview && variant !== 'simulator',
    'bg-stone-200': !isGleichTonOrOrientierungston.value && !isActive && !isPreview,
  }
})

// Accessible Name (a11y): die sichtbaren Label-Spans sind v-if-gated und oft leer, daher
// baut sich der Screenreader-Name aus den immer vorhandenen Knopf-Daten (Reihe/Nummer/Töne).
const ariaLabel = computed(
    () =>
        `Reihe ${rowIdx + 1}, Knopf ${btnIdx + 1}, ` +
        `Druck ${btnData.druck.join(' / ')}, Zug ${btnData.zug.join(' / ')}`,
)

function activate() {
  emit('activate', rowIdx, btnIdx)
}

function deactivate() {
  emit('deactivate', rowIdx, btnIdx)
}
</script>

<template>
  <button
      type="button"
      :aria-label="ariaLabel"
      class="relative group   flex-col items-center justify-center rounded-full border-2 border-stone-300 text-center text-[9px] shadow-[0_4px_5px_rgba(0,0,0,0.3),inset_0_-2px_5px_rgba(0,0,0,0.2)] transition-all hover:bg-teal-100"
      :class="[sizeClasses, stateClasses]"
      @mousedown="activate"
      @mouseup="deactivate"
      @mouseleave="deactivate"
      @touchstart.prevent="activate"
      @touchend.prevent="deactivate"
  >

    <span
        v-if="matchesDruck"
        class="absolute -top-1 -left-1 z-20 h-3 w-3 animate-pulse rounded-full border-2 border-stone-800 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] md:h-4 md:w-4"
        title="Gleicher Ton auf DRUCK"
    />

    <span
        v-if="matchesZug"
        class="border-stone-800 bg-blue-500 absolute  -top-1 -right-1 z-20 h-3 w-3 animate-pulse rounded-full border-2 shadow-[0_0_8px_rgba(59,130,246,0.8)] md:h-4 md:w-4"

        title="Gleicher Ton auf ZUG"
    />

    <span
        class="flex flex-col h-full items-center justify-center p-0 text-center leading-none font-bold transition-all duration-200"
        :class="{'grid-rows-[30%_auto]': keyLabel && label}"
    >
      <span v-if="keyLabel" class="font-mono text-[8px] text-stone-400 uppercase">
        {{ keyLabel }}
      </span>
      <span v-if="label" class="inline-flex items-center whitespace-pre-line text-center text-md font-bold">
        {{ label }}
      </span>
      <span v-else class="inline-flex items-center whitespace-pre-line text-center text-md scale-80">
<span class="opacity-0 group group-hover:opacity-100 hover:opacity-100">        {{ rowIdx }} {{ btnIdx }}</span>
      </span>

    </span>
  </button>
</template>
